/**
 * Invented data for the analytics screens.
 *
 * NOTHING HERE IS REAL. No user or event models exist in the API, so these
 * numbers were written by hand to give the screens something to be judged
 * against. They are deliberately not a clean curve: the assessment loses most
 * of its users in the first three turns, registration is weighted heavily
 * toward the first session, a third of trial losses are failed payments rather
 * than decisions, and the two newest cohorts are too young to have reached the
 * later rungs. Those are the states the layout has to survive.
 *
 * Every screen that reads this file renders a SampleDataBanner saying so.
 */

import type { DropOff, Journey, Signals } from '@/lib/analytics';

export const dropOff: DropOff = {
  window: 'All users whose first run fell in the last 28 days',
  assessment: {
    reached: 2359,
    completed: 1386,
    bounced: 973,
    // The shape that matters: two thirds of the loss is in the first three
    // turns, which is a cold-open problem rather than a difficulty problem.
    buckets: [
      { turn: 1, users: 402 },
      { turn: 2, users: 311 },
      { turn: 3, users: 118 },
      { turn: 4, users: 52 },
      { turn: 5, users: 34 },
      { turn: 6, users: 21 },
      { turn: 7, users: 15 },
      { turn: 8, users: 11 },
      { turn: 9, users: 6 },
      { turn: 10, users: 3 },
    ],
  },
  registration: {
    inFirstSession: 402,
    later: 187,
    chose: 526,
    requiredToSubscribe: 63,
  },
  trial: {
    started: 219,
    converted: 138,
    cancelledVoluntary: 57,
    failedInvoluntary: 24,
  },
};

export const signals: Signals = {
  window: 'Last 28 days',
  firstRuns: 2359,
  secondSessions: 1043,
  installs: {
    total: 3104,
    source: 'App Store Connect and Play Console',
    caveat:
      'A different system with a different denominator. Not a funnel step, and not comparable to first runs — a reinstall is a new first run but not a new install, and an install that never opens is invisible here.',
  },
  coverage: [
    {
      surface: 'Characters',
      touched: 1201,
      counted: 908,
      bar: 'Six or more turns with user-produced target language',
    },
    {
      surface: 'The coach',
      touched: 1109,
      counted: 963,
      bar: 'Answered at least one thing the coach asked for',
    },
    {
      surface: 'Articles',
      touched: 742,
      counted: 388,
      bar: 'Read to the end, or three or more word taps',
    },
    {
      surface: 'Audio',
      touched: 511,
      counted: 274,
      bar: 'Played past 80% of the clip',
    },
    {
      surface: 'Party games',
      touched: 306,
      counted: 214,
      bar: 'Finished a round',
    },
  ],
  levelMoves: [
    { from: 'The waiter', to: 'Sofia', users: 214, medianDays: 11 },
    { from: 'Sofia', to: 'Marco', users: 63, medianDays: 26 },
    { from: 'Marco', to: 'Yusuf', users: 9, medianDays: 41 },
  ],
};

export const journeys: Journey[] = [
  {
    id: 'j-8f2a',
    anonymousId: 'anon_8f2a…c41d',
    label: 'Anonymous user',
    language: 'Spanish',
    platform: 'iOS',
    firstRunAt: '18 Aug, 07:42',
    reached: 'first-run',
    headline: 'Bounced two turns into the café and never came back.',
    events: [
      {
        id: 'e1',
        at: '18 Aug, 07:42:03',
        title: 'First run',
        detail: 'iOS 26.1, device-scoped anonymous user created.',
        phase: 'anonymous',
        milestone: 'first-run',
      },
      {
        id: 'e2',
        at: '18 Aug, 07:42:19',
        title: 'Cold open passed',
        phase: 'anonymous',
      },
      {
        id: 'e3',
        at: '18 Aug, 07:43:11',
        title: 'Act I answered',
        detail: 'Spanish · moving abroad · two headlines picked.',
        phase: 'anonymous',
      },
      {
        id: 'e4',
        at: '18 Aug, 07:44:02',
        title: 'Act II opened',
        detail: 'Scripted turns 1–2 served in 180 ms.',
        phase: 'anonymous',
      },
      {
        id: 'e5',
        at: '18 Aug, 07:44:37',
        title: 'Turn 3 generated',
        detail: 'First model turn took 4.9 s. Above target.',
        phase: 'anonymous',
      },
      {
        id: 'e6',
        at: '18 Aug, 07:44:58',
        title: 'Act II abandoned at turn 3',
        detail: 'No lifeline used. App backgrounded and not reopened.',
        phase: 'anonymous',
      },
    ],
  },
  {
    id: 'j-3c17',
    anonymousId: 'anon_3c17…9ab2',
    label: 'r••••@gmail.com',
    language: 'Spanish',
    platform: 'Android',
    firstRunAt: '11 Aug, 20:15',
    reached: 'payment',
    headline:
      'Finished, came back three times, signed in on day four, paid on day eleven.',
    events: [
      {
        id: 'e1',
        at: '11 Aug, 20:15:44',
        title: 'First run',
        detail: 'Android 16, device-scoped anonymous user created.',
        phase: 'anonymous',
        milestone: 'first-run',
      },
      {
        id: 'e2',
        at: '11 Aug, 20:19:02',
        title: 'Assessment complete',
        detail: '12 turns, one lifeline used, estimated A2.',
        phase: 'anonymous',
        milestone: 'assessment',
      },
      {
        id: 'e3',
        at: '11 Aug, 20:20:10',
        title: 'Sign-in skipped',
        detail: 'Offered at the end of Act III and declined.',
        phase: 'anonymous',
      },
      {
        id: 'e4',
        at: '12 Aug, 08:04',
        title: 'Second session',
        detail: 'Conversation with the waiter, 14 turns.',
        phase: 'anonymous',
      },
      {
        id: 'e5',
        at: '13 Aug, 21:31',
        title: 'Article read',
        detail: 'Read to the end. 6 word taps.',
        phase: 'anonymous',
      },
      {
        id: 'e6',
        at: '15 Aug, 19:12',
        title: 'Account adopted this user',
        detail:
          'Google sign-in. The anonymous events above were re-parented to the account; nothing was rewritten.',
        phase: 'adoption',
        milestone: 'registration',
      },
      {
        id: 'e7',
        at: '18 Aug, 07:55',
        title: 'Moved up a level',
        detail: 'The waiter → Sofia, 7 days after first run.',
        phase: 'account',
      },
      {
        id: 'e8',
        at: '22 Aug, 18:40',
        title: 'Trial started',
        detail: 'Seven-day trial. Payment details given.',
        phase: 'account',
        milestone: 'subscription',
      },
      {
        id: 'e9',
        at: '29 Aug, 18:41',
        title: 'First payment taken',
        detail: 'Trial lapsed into paid.',
        phase: 'account',
        milestone: 'payment',
      },
    ],
  },
  {
    id: 'j-b904',
    anonymousId: 'anon_b904…7e55',
    label: 'm••••@icloud.com',
    language: 'Italian',
    platform: 'iOS',
    firstRunAt: '20 Aug, 12:03',
    reached: 'registration',
    headline:
      'Signed into an account that already had history — two anonymous chains folded into one.',
    events: [
      {
        id: 'e1',
        at: '20 Aug, 12:03:18',
        title: 'First run',
        detail: 'Second device for this person, though nothing knew that yet.',
        phase: 'anonymous',
        milestone: 'first-run',
      },
      {
        id: 'e2',
        at: '20 Aug, 12:07:55',
        title: 'Assessment complete',
        detail: '9 turns, estimated B1 — higher than the first device recorded.',
        phase: 'anonymous',
        milestone: 'assessment',
      },
      {
        id: 'e3',
        at: '20 Aug, 12:08:40',
        title: 'Signed into an existing account',
        detail:
          'The account already carried 4 months of history from anon_1d77…0c9e. Both chains now resolve to one user; neither was discarded.',
        phase: 'adoption',
        milestone: 'registration',
      },
      {
        id: 'e4',
        at: '20 Aug, 12:09',
        title: 'Level estimate reconciled',
        detail:
          'Two estimates, 6 weeks apart, from two devices. The later one wins; the earlier is kept.',
        phase: 'account',
      },
      {
        id: 'e5',
        at: '26 Aug, 21:14',
        title: 'Conversation',
        detail: 'Marco, 22 turns, no lifelines.',
        phase: 'account',
      },
    ],
  },
  {
    id: 'j-51de',
    anonymousId: 'anon_51de…2f80',
    label: 'k••••@gmail.com',
    language: 'Japanese',
    platform: 'iOS',
    firstRunAt: '25 Aug, 09:27',
    reached: 'subscription',
    headline: 'Registered only because subscribing required it. Trial ends today.',
    events: [
      {
        id: 'e1',
        at: '25 Aug, 09:27:02',
        title: 'First run',
        phase: 'anonymous',
        milestone: 'first-run',
      },
      {
        id: 'e2',
        at: '25 Aug, 09:31:48',
        title: 'Assessment complete',
        detail: '16 turns, no lifelines, estimated B1.',
        phase: 'anonymous',
        milestone: 'assessment',
      },
      {
        id: 'e3',
        at: '25 Aug, 09:32:30',
        title: 'Sign-in skipped',
        phase: 'anonymous',
      },
      {
        id: 'e4',
        at: '25 Aug, 09:48',
        title: 'Purchase completed at the store',
        detail:
          'Tied to an Apple account. For 41 seconds a paying device had no identity of ours.',
        phase: 'anonymous',
      },
      {
        id: 'e5',
        at: '25 Aug, 09:49',
        title: 'Account created to carry the purchase',
        detail: 'Trigger: required to subscribe, not chosen.',
        phase: 'adoption',
        milestone: 'registration',
      },
      {
        id: 'e6',
        at: '25 Aug, 09:49:06',
        title: 'Trial started',
        detail: 'Seven-day trial. Ends today.',
        phase: 'account',
        milestone: 'subscription',
      },
    ],
  },
  {
    id: 'j-2a68',
    anonymousId: 'anon_2a68…b013',
    label: 'Anonymous user',
    language: 'French',
    platform: 'Android',
    firstRunAt: '30 Aug, 22:51',
    reached: 'assessment',
    headline: 'Finished the assessment, has not come back. Two days old.',
    events: [
      {
        id: 'e1',
        at: '30 Aug, 22:51:09',
        title: 'First run',
        phase: 'anonymous',
        milestone: 'first-run',
      },
      {
        id: 'e2',
        at: '30 Aug, 22:55:31',
        title: 'Assessment complete',
        detail: '8 turns, all three lifelines used, estimated A1.',
        phase: 'anonymous',
        milestone: 'assessment',
      },
      {
        id: 'e3',
        at: '30 Aug, 22:56',
        title: 'Sign-in skipped',
        phase: 'anonymous',
      },
    ],
  },
  {
    id: 'j-c7f1',
    anonymousId: 'anon_c7f1…44a9',
    label: 'Anonymous user',
    language: 'Spanish',
    platform: 'Android',
    firstRunAt: '1 Sep, 06:18',
    reached: 'first-run',
    headline: 'Opened the app this morning and closed it on the first screen.',
    events: [
      {
        id: 'e1',
        at: '1 Sep, 06:18:40',
        title: 'First run',
        detail: 'One server call, 11 seconds of session.',
        phase: 'anonymous',
        milestone: 'first-run',
      },
      {
        id: 'e2',
        at: '1 Sep, 06:18:51',
        title: 'Closed on the cold open',
        detail: 'No language chosen. Counts in the denominator regardless.',
        phase: 'anonymous',
      },
    ],
  },
];
